import { useEffect, useState } from "react";

import { ChevronDown, ChevronRight, Layers } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { supabase } from "@/integrations/supabase/client";
import { getCategoryConfig } from "@/lib/memoryCategories";

interface Memory {
  id: string;
  content: string;
  category: string;
  enabled: boolean;
}

interface InheritanceLevel {
  level: "workspace" | "board" | "folder" | "tag" | "project";
  label: string;
  memories: Memory[];
  color: string;
}

interface MemoryInheritanceVisualizationProps {
  workspaceId?: string;
  boardId?: string;
  folderId?: string;
  projectId?: string;
}

export const MemoryInheritanceVisualization = ({
  workspaceId,
  boardId,
  folderId,
  projectId,
}: MemoryInheritanceVisualizationProps) => {
  const [inheritanceLevels, setInheritanceLevels] = useState<InheritanceLevel[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedLevels, setExpandedLevels] = useState<Set<string>>(new Set(["workspace", "board", "folder", "project"]));

  useEffect(() => {
    fetchInheritedMemories();
  }, [workspaceId, boardId, folderId, projectId]);

  const fetchInheritedMemories = async () => {
    setLoading(true);
    try {
      const levels: InheritanceLevel[] = [];

      // Determine context hierarchy
      let contextWorkspaceId = workspaceId;
      let contextBoardId = boardId;
      let contextFolderId = folderId;
      let tagIds: string[] = [];

      // If we have a project, fetch its context
      if (projectId) {
        const { data: projectData } = await supabase
          .from("projects")
          .select(`
            id,
            board_id,
            folder_id,
            boards!inner(
              id,
              workspace_id
            )
          `)
          .eq("id", projectId)
          .single();

        if (projectData) {
          contextWorkspaceId = projectData.boards.workspace_id;
          contextBoardId = projectData.board_id;
          contextFolderId = projectData.folder_id;

          // Get tags
          const { data: tagData } = await supabase
            .from("project_tags")
            .select("tag_id")
            .eq("project_id", projectId);
          tagIds = tagData?.map(t => t.tag_id) || [];
        }
      }

      // Fetch workspace memories
      if (contextWorkspaceId) {
        const { data } = await supabase
          .from("memories")
          .select("id, content, category, enabled")
          .eq("workspace_id", contextWorkspaceId)
          .eq("enabled", true);

        if (data && data.length > 0) {
          levels.push({
            level: "workspace",
            label: "Workspace",
            memories: data,
            color: "oklch(0.75 0.15 270)",
          });
        }
      }

      // Fetch board memories
      if (contextBoardId) {
        const { data } = await supabase
          .from("memories")
          .select("id, content, category, enabled")
          .eq("board_id", contextBoardId)
          .eq("enabled", true);

        if (data && data.length > 0) {
          levels.push({
            level: "board",
            label: "Board",
            memories: data,
            color: "oklch(0.65 0.2 240)",
          });
        }
      }

      // Fetch folder memories
      if (contextFolderId) {
        const { data } = await supabase
          .from("memories")
          .select("id, content, category, enabled")
          .eq("folder_id", contextFolderId)
          .eq("enabled", true);

        if (data && data.length > 0) {
          levels.push({
            level: "folder",
            label: "Folder",
            memories: data,
            color: "oklch(0.70 0.18 180)",
          });
        }
      }

      // Fetch tag memories
      if (tagIds.length > 0) {
        const { data } = await supabase
          .from("memories")
          .select("id, content, category, enabled")
          .in("tag_id", tagIds)
          .eq("enabled", true);

        if (data && data.length > 0) {
          levels.push({
            level: "tag",
            label: "Tags",
            memories: data,
            color: "oklch(0.72 0.20 150)",
          });
        }
      }

      // Fetch project memories
      if (projectId) {
        const { data } = await supabase
          .from("memories")
          .select("id, content, category, enabled")
          .eq("project_id", projectId)
          .eq("enabled", true);

        if (data && data.length > 0) {
          levels.push({
            level: "project",
            label: "Project",
            memories: data,
            color: "oklch(0.65 0.20 30)",
          });
        }
      }

      setInheritanceLevels(levels);
    } catch (error) {
      console.error("Error fetching inherited memories:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleLevel = (level: string) => {
    setExpandedLevels(prev => {
      const next = new Set(prev);
      if (next.has(level)) {
        next.delete(level);
      } else {
        next.add(level);
      }
      return next;
    });
  };

  if (loading) {
    return (
      <Card className="p-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Layers className="h-4 w-4 animate-pulse" />
          Loading inheritance...
        </div>
      </Card>
    );
  }

  if (inheritanceLevels.length === 0) {
    return null;
  }

  const totalMemories = inheritanceLevels.reduce((sum, level) => sum + level.memories.length, 0);

  return (
    <Card className="p-4">
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Layers className="h-4 w-4 text-muted-foreground" />
            <h4 className="font-semibold text-sm">Memory Inheritance</h4>
          </div>
          <Badge variant="secondary">{totalMemories} total</Badge>
        </div>

        <div className="space-y-2">
          {inheritanceLevels.map((level, index) => {
            const isExpanded = expandedLevels.has(level.level);
            
            return (
              <Collapsible
                key={level.level}
                open={isExpanded}
                onOpenChange={() => toggleLevel(level.level)}
              >
                <div className="space-y-2">
                  <CollapsibleTrigger asChild>
                    <Button
                      variant="ghost"
                      className="w-full justify-between h-auto py-2 px-3"
                    >
                      <div className="flex items-center gap-2">
                        {isExpanded ? (
                          <ChevronDown className="h-3 w-3" />
                        ) : (
                          <ChevronRight className="h-3 w-3" />
                        )}
                        <Badge
                          variant="secondary"
                          style={{
                            backgroundColor: `${level.color}15`,
                            color: level.color,
                            borderColor: `${level.color}40`,
                          }}
                        >
                          {level.label}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {level.memories.length} {level.memories.length === 1 ? "memory" : "memories"}
                        </span>
                      </div>
                    </Button>
                  </CollapsibleTrigger>

                  <CollapsibleContent className="space-y-1 ml-5 pl-3 border-l-2 border-border">
                    {level.memories.map((memory) => {
                      const categoryConfig = getCategoryConfig(memory.category as any);
                      const CategoryIcon = categoryConfig.icon;

                      return (
                        <div
                          key={memory.id}
                          className="p-2 bg-muted/50 rounded text-xs space-y-1"
                        >
                          <Badge
                            variant="outline"
                            className="gap-1"
                            style={{
                              borderColor: categoryConfig.color,
                              color: categoryConfig.color,
                            }}
                          >
                            <CategoryIcon className="h-3 w-3" />
                            {categoryConfig.label}
                          </Badge>
                          <p className="text-muted-foreground line-clamp-2">
                            {memory.content}
                          </p>
                        </div>
                      );
                    })}
                  </CollapsibleContent>
                </div>
              </Collapsible>
            );
          })}
        </div>

        <div className="pt-2 border-t border-border">
          <p className="text-xs text-muted-foreground">
            Memories cascade from parent scopes. Higher levels apply to all nested items.
          </p>
        </div>
      </div>
    </Card>
  );
};
